import React, { useMemo, useState } from "react";
import { getFacility360Workers } from "../utils/facility360Metrics";

function normalize(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_");
}

function titleCase(value) {
  return String(value || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (letter) =>
      letter.toUpperCase()
    );
}

function getWorkerId(worker) {
  return String(worker?.uid || worker?.id || "");
}

function getWorkerName(worker) {
  return (
    worker?.displayName ||
    worker?.fullName ||
    [worker?.firstName, worker?.lastName]
      .filter(Boolean)
      .join(" ") ||
    worker?.email ||
    "Worker"
  );
}

function getWorkerRole(worker) {
  return String(
    worker?.primaryRole ||
      worker?.position ||
      worker?.workerType ||
      worker?.requiredPosition ||
      "Worker"
  ).toUpperCase();
}

function getOperationalStatus(worker) {
  return normalize(
    worker?.workerOperationalState ||
      worker?.workerStatus ||
      worker?.status
  );
}

function isWorkerReady(worker) {
  return [
    "ready",
    "active",
    "ready_for_shifts",
  ].includes(
    getOperationalStatus(worker)
  );
}

function workerFacilityMembership(
  worker,
  facilityId
) {
  const primaryFacilityId = normalize(
    worker?.facilityId ||
      worker?.primaryFacilityId ||
      worker?.assignedFacilityId
  );

  const permittedFacilityIds = [
    ...(Array.isArray(worker?.facilityIds)
      ? worker.facilityIds
      : []),
    ...(Array.isArray(
      worker?.allowedFacilityIds
    )
      ? worker.allowedFacilityIds
      : []),
    ...(Array.isArray(
      worker?.assignedFacilityIds
    )
      ? worker.assignedFacilityIds
      : []),
  ]
    .map(normalize)
    .filter(Boolean);

  if (primaryFacilityId === facilityId) {
    return "primary";
  }

  if (
    permittedFacilityIds.includes(facilityId)
  ) {
    return "permitted";
  }

  return "";
}

function MetricCard({
  label,
  value,
  color = "#0f172a",
}) {
  return (
    <div
      style={{
        padding: 13,
        borderRadius: 14,
        background: "#f8fafc",
        border: "1px solid #e2e8f0",
      }}
    >
      <div
        style={{
          color: "#64748b",
          fontSize: 10,
          fontWeight: 900,
          textTransform: "uppercase",
        }}
      >
        {label}
      </div>

      <div
        style={{
          marginTop: 6,
          color,
          fontSize: 21,
          fontWeight: 950,
        }}
      >
        {value}
      </div>
    </div>
  );
}

function RelationshipButton({
  active,
  activeColor,
  activeBackground,
  label,
  activeLabel,
  onClick,
  disabled = false,
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      style={{
        minHeight: 32,
        padding: "6px 9px",
        borderRadius: 10,
        cursor: disabled
          ? "not-allowed"
          : "pointer",
        opacity: disabled ? 0.5 : 1,
        color: active
          ? activeColor
          : "#334155",
        background: active
          ? activeBackground
          : "#ffffff",
        border: active
          ? `1px solid ${activeColor}`
          : "1px solid #cbd5e1",
        fontSize: 10,
        fontWeight: 900,
      }}
    >
      {active ? activeLabel : label}
    </button>
  );
}

export default function Facility360Workforce({
  facility,
  users = [],
  onChangeFacility,
}) {
  const [search, setSearch] = useState("");

  const [statusFilter, setStatusFilter] =
    useState("all");

  const facilityId = normalize(
    facility?.id || facility?.facilityId
  );

  const favoriteWorkerUids = Array.isArray(
    facility?.favoriteWorkerUids
  )
    ? facility.favoriteWorkerUids
    : [];

  const blockedWorkerUids = Array.isArray(
    facility?.blockedWorkerUids
  )
    ? facility.blockedWorkerUids
    : [];

  const favoriteSet = useMemo(
    () =>
      new Set(
        favoriteWorkerUids.map(normalize)
      ),
    [favoriteWorkerUids]
  );

  const blockedSet = useMemo(
    () =>
      new Set(
        blockedWorkerUids.map(normalize)
      ),
    [blockedWorkerUids]
  );

  const workforce = useMemo(() => {
    return getFacility360Workers(
      facility,
      users
    )
      .map((worker) => {
        const workerId = normalize(
          getWorkerId(worker)
        );

        return {
          ...worker,
          facilityMembership:
            workerFacilityMembership(
              worker,
              facilityId
            ),
          isFavorite:
            favoriteSet.has(workerId),
          isBlocked:
            blockedSet.has(workerId),
        };
      })
      .sort((a, b) =>
        getWorkerName(a).localeCompare(
          getWorkerName(b)
        )
      );
  }, [
    facility,
    users,
    facilityId,
    favoriteSet,
    blockedSet,
  ]);

  const filteredWorkforce = useMemo(() => {
    const term = normalize(search);

    return workforce.filter((worker) => {
  const matchesStatus =
  statusFilter === "all" ||
  (statusFilter === "ready" &&
    isWorkerReady(worker)) ||
  (statusFilter === "attention" &&
    !isWorkerReady(worker)) ||
  (statusFilter === "favorite" &&
    worker.isFavorite) ||
  (statusFilter === "blocked" &&
    worker.isBlocked);

      const searchable = normalize(
        [
          getWorkerName(worker),
          worker?.email,
          getWorkerRole(worker),
          worker?.complianceStatus,
          worker?.facilityMembership,
          worker.isFavorite ? "favorite" : "",
          worker.isBlocked ? "blocked" : "",
        ]
          .filter(Boolean)
          .join(" ")
      );

      return (
        matchesStatus &&
        (!term || searchable.includes(term))
      );
    });
  }, [workforce, search, statusFilter]);

  const readyCount = workforce.filter(
  isWorkerReady
).length;

  const compliantCount = workforce.filter(
    (worker) =>
      normalize(worker?.complianceStatus) ===
      "approved"
  ).length;

  const primaryCount = workforce.filter(
    (worker) =>
      worker.facilityMembership === "primary"
  ).length;

  const favoriteCount = workforce.filter(
    (worker) => worker.isFavorite
  ).length;

  const blockedCount = workforce.filter(
    (worker) => worker.isBlocked
  ).length;

  const attentionCount =
    workforce.length - readyCount;

  function updateRelationships(
    worker,
    relationship
  ) {
    if (
      typeof onChangeFacility !== "function"
    ) {
      return;
    }

    const workerId = getWorkerId(worker);
    const normalizedWorkerId =
      normalize(workerId);

    if (!normalizedWorkerId) return;

    onChangeFacility((previousFacility) => {
      const currentFavorites = Array.isArray(
        previousFacility?.favoriteWorkerUids
      )
        ? previousFacility.favoriteWorkerUids
        : [];

      const currentBlocked = Array.isArray(
        previousFacility?.blockedWorkerUids
      )
        ? previousFacility.blockedWorkerUids
        : [];

      const favoriteIds =
        currentFavorites.filter(
          (id) =>
            normalize(id) !==
            normalizedWorkerId
        );

      const blockedIds =
        currentBlocked.filter(
          (id) =>
            normalize(id) !==
            normalizedWorkerId
        );

      const currentlyFavorite =
        currentFavorites.some(
          (id) =>
            normalize(id) ===
            normalizedWorkerId
        );

      const currentlyBlocked =
        currentBlocked.some(
          (id) =>
            normalize(id) ===
            normalizedWorkerId
        );

      if (relationship === "favorite") {
        return {
          ...previousFacility,
          favoriteWorkerUids:
            currentlyFavorite
              ? favoriteIds
              : [...favoriteIds, workerId],
          blockedWorkerUids: blockedIds,
        };
      }

      if (relationship === "blocked") {
        return {
          ...previousFacility,
          favoriteWorkerUids:
            favoriteIds,
          blockedWorkerUids:
            currentlyBlocked
              ? blockedIds
              : [...blockedIds, workerId],
        };
      }

      return previousFacility;
    });
  }

  return (
    <div style={{ display: "grid", gap: 14 }}>
      <div>
        <div
          style={{
            color: "#0f172a",
            fontSize: 20,
            fontWeight: 950,
          }}
        >
          Facility Workforce
        </div>

        <div
          style={{
            marginTop: 4,
            color: "#64748b",
            fontSize: 12,
            fontWeight: 700,
          }}
        >
          Workers assigned or permitted to work at{" "}
          {facility?.name ||
            facility?.facilityName ||
            facility?.id ||
            "this facility"}
          .
        </div>

        <div
          style={{
            marginTop: 5,
            color: "#7c3aed",
            fontSize: 10,
            fontWeight: 800,
          }}
        >
          Relationship changes are saved when
          you select Save.
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns:
            "repeat(2, minmax(0, 1fr))",
          gap: 9,
        }}
      >
        <MetricCard
          label="Total Workforce"
          value={workforce.length}
        />

        <MetricCard
          label="Ready"
          value={readyCount}
          color="#166534"
        />

        <MetricCard
          label="Compliant"
          value={compliantCount}
          color="#1d4ed8"
        />

        <MetricCard
          label="Needs Attention"
          value={attentionCount}
          color={
            attentionCount > 0
              ? "#b45309"
              : "#166534"
          }
        />

        <MetricCard
          label="Primary Workers"
          value={primaryCount}
          color="#7c3aed"
        />

        <MetricCard
          label="Permitted Workers"
          value={
            workforce.length - primaryCount
          }
          color="#0369a1"
        />

        <MetricCard
          label="Favorites"
          value={favoriteCount}
          color="#7c3aed"
        />

        <MetricCard
          label="Blocked"
          value={blockedCount}
          color={
            blockedCount > 0
              ? "#b91c1c"
              : "#166534"
          }
        />
      </div>

      <input
        type="text"
        value={search}
        onChange={(event) =>
          setSearch(event.target.value)
        }
        placeholder="Search facility workforce..."
        style={{
          width: "100%",
          minHeight: 40,
          padding: "0 12px",
          borderRadius: 12,
          color: "#0f172a",
          background: "#ffffff",
          border: "1px solid #cbd5e1",
          boxSizing: "border-box",
          fontWeight: 700,
        }}
      />

      <div
        style={{
          display: "flex",
          gap: 7,
          overflowX: "auto",
        }}
      >
        {[
          ["all", "All"],
          ["ready", "Ready"],
          ["attention", "Needs Attention"],
          ["favorite", "Favorites"],
          ["blocked", "Blocked"],
        ].map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() =>
              setStatusFilter(key)
            }
            style={{
              minHeight: 34,
              padding: "6px 11px",
              borderRadius: 999,
              whiteSpace: "nowrap",
              cursor: "pointer",
              color:
                statusFilter === key
                  ? "#ffffff"
                  : "#0f172a",
              background:
                statusFilter === key
                  ? "#2563eb"
                  : "#ffffff",
              border:
                statusFilter === key
                  ? "1px solid #2563eb"
                  : "1px solid #cbd5e1",
              fontSize: 11,
              fontWeight: 900,
            }}
          >
            {label}
          </button>
        ))}
      </div>

      {filteredWorkforce.length === 0 ? (
        <div
          style={{
            padding: 20,
            borderRadius: 14,
            textAlign: "center",
            color: "#64748b",
            background: "#f8fafc",
            border: "1px dashed #cbd5e1",
            fontWeight: 750,
          }}
        >
          No matching workers were found.
        </div>
      ) : (
        <div
          style={{
            display: "grid",
            gap: 9,
          }}
        >
          {filteredWorkforce.map((worker) => {
            const workerId =
              getWorkerId(worker);

            const operationalStatus =
              getOperationalStatus(worker);

            const complianceStatus =
              normalize(
                worker?.complianceStatus
              );

            const ready =
              isWorkerReady(worker);

            const compliant =
              complianceStatus === "approved";

            const reliability =
              worker?.reliabilityScore ??
              worker?.reliability ??
              worker?.reliabilityPercent ??
              "—";

            return (
              <div
                key={workerId}
                style={{
                  padding: 13,
                  borderRadius: 14,
                  background: worker.isBlocked
                    ? "#fff7f7"
                    : worker.isFavorite
                    ? "#faf5ff"
                    : "#ffffff",
                  border: worker.isBlocked
                    ? "1px solid #fecaca"
                    : worker.isFavorite
                    ? "1px solid #ddd6fe"
                    : "1px solid #e2e8f0",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent:
                      "space-between",
                    alignItems: "flex-start",
                    gap: 10,
                  }}
                >
                  <div>
                    <div
                      style={{
                        color: "#0f172a",
                        fontSize: 14,
                        fontWeight: 950,
                      }}
                    >
                      {getWorkerName(worker)}
                    </div>

                    <div
                      style={{
                        marginTop: 4,
                        color: "#475569",
                        fontSize: 12,
                        fontWeight: 800,
                      }}
                    >
                      {getWorkerRole(worker)}
                      {" · "}
                      {titleCase(
                        worker.facilityMembership
                      )}
                    </div>

                    <div
                      style={{
                        marginTop: 4,
                        color: "#64748b",
                        fontSize: 11,
                        fontWeight: 700,
                      }}
                    >
                      {worker.email ||
                        "Email unavailable"}
                    </div>

                    <div
                      style={{
                        marginTop: 5,
                        color: "#475569",
                        fontSize: 11,
                        fontWeight: 800,
                      }}
                    >
                      Reliability: {reliability}
                    </div>
                  </div>

                  <div
                    style={{
                      display: "grid",
                      justifyItems: "end",
                      gap: 6,
                    }}
                  >
                    <div
                      style={{
                        padding: "5px 8px",
                        borderRadius: 999,
                        color: ready
                          ? "#166534"
                          : "#92400e",
                        background: ready
                          ? "#dcfce7"
                          : "#fef3c7",
                        fontSize: 9,
                        fontWeight: 950,
                        textTransform:
                          "uppercase",
                      }}
                    >
                      {operationalStatus ||
                        "Unknown"}
                    </div>

                    <div
                      style={{
                        padding: "5px 8px",
                        borderRadius: 999,
                        color: compliant
                          ? "#166534"
                          : "#991b1b",
                        background: compliant
                          ? "#dcfce7"
                          : "#fee2e2",
                        fontSize: 9,
                        fontWeight: 950,
                        textTransform:
                          "uppercase",
                      }}
                    >
                      {compliant
                        ? "Compliant"
                        : titleCase(
                            complianceStatus ||
                              "Not Approved"
                          )}
                    </div>
                  </div>
                </div>

                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 7,
                    marginTop: 11,
                    paddingTop: 10,
                    borderTop:
                      "1px solid #e2e8f0",
                  }}
                >
                  <RelationshipButton
                    active={worker.isFavorite}
                    activeColor="#7c3aed"
                    activeBackground="#ede9fe"
                    label="Mark Favorite"
                    activeLabel="Remove Favorite"
                    disabled={!workerId}
                    onClick={() =>
                      updateRelationships(
                        worker,
                        "favorite"
                      )
                    }
                  />

                  <RelationshipButton
                    active={worker.isBlocked}
                    activeColor="#b91c1c"
                    activeBackground="#fee2e2"
                    label="Block Worker"
                    activeLabel="Unblock Worker"
                    disabled={!workerId}
                    onClick={() =>
                      updateRelationships(
                        worker,
                        "blocked"
                      )
                    }
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
